import { useEffect, useState } from "react";

import { listCostCenterGroupsApi, listCostCentersApi } from "../api/financeiroApi";
import type { FinCostCenter, FinCostCenterGroup } from "../api/finTypes";

/** Opções do `<select>` de centro de custo, em `<optgroup>` por grupo. */
export type CostCenterOptionGroup = { key: string; label: string; options: FinCostCenter[] };

const byCode = (a: FinCostCenter, b: FinCostCenter) => a.code.localeCompare(b.code, "pt-BR", { numeric: true });

/**
 * Agrupa os centros pelo grupo ("Operacional"), na ordem dos grupos; os sem grupo
 * (ou de grupo inativo) vão por último, em "Sem grupo".
 */
export function groupCostCenterOptions(costCenters: FinCostCenter[], groups: FinCostCenterGroup[]): CostCenterOptionGroup[] {
  const out: CostCenterOptionGroup[] = [];
  const placed = new Set<string>();
  const push = (key: string, label: string, groupId: string) => {
    const options = costCenters.filter((c) => c.group?.id === groupId).sort(byCode);
    options.forEach((o) => placed.add(o.id));
    if (options.length) out.push({ key, label, options });
  };
  for (const g of groups) push(g.id, `${g.code} — ${g.name}`, g.id);
  const rest = costCenters.filter((c) => !placed.has(c.id)).sort(byCode);
  if (rest.length) out.push({ key: "none", label: groups.length ? "Sem grupo" : "Centros de custo", options: rest });
  return out;
}

/** "2 Operacional › 2 — Rio de Janeiro" a partir do centro como o título o devolve. */
export function costCenterPath(cc: {
  code: string;
  name: string;
  group?: { code?: string; name: string } | null;
} | null): string {
  if (!cc) return "—";
  const group = cc.group ? `${cc.group.code ? `${cc.group.code} ` : ""}${cc.group.name}` : null;
  return [group, `${cc.code} — ${cc.name}`].filter(Boolean).join(" › ");
}

/** Centros de custo ativos e os grupos, carregados juntos. */
export function useCostCenters(token: string) {
  const [costCenters, setCostCenters] = useState<FinCostCenter[]>([]);
  const [groups, setGroups] = useState<FinCostCenterGroup[]>([]);
  useEffect(() => {
    if (!token) return;
    listCostCentersApi(token).then(setCostCenters).catch(() => setCostCenters([]));
    listCostCenterGroupsApi(token).then(setGroups).catch(() => setGroups([]));
  }, [token]);
  return { costCenters, groups };
}

/** Ids dos centros de um grupo (para filtros locais). */
export function costCenterIdsOfGroup(groups: FinCostCenterGroup[], groupId: string): string[] {
  return groups.find((g) => g.id === groupId)?.costCenters.map((c) => c.id) ?? [];
}
